import React, { useRef, useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { PageBanners } from '../../types';
import { Pause, Play } from 'lucide-react';
import dashboardHeroBg from '../../assets/images/student_wbl_opportunity_1782528661429.jpg';

interface HeroBannerProps {
  title: string;
  subtitle?: string;
  pageKey?: keyof PageBanners;
  children?: React.ReactNode;
  height?: string;
  overlayOpacity?: number;
  className?: string;
  termCounts?: Record<string, number>;
  hideTermSelector?: boolean;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({ 
  title, 
  subtitle, 
  pageKey,
  children, 
  height = '187.5px',
  overlayOpacity = 0.6,
  className = "",
  termCounts,
  hideTermSelector = false
}) => {
  const { 
    globalBranding, 
    activeTenant, 
    selectedTerm, 
    setSelectedTerm, 
    systemConfig,
    opportunities = [],
    applications = [],
    placements = [],
    interviews = [],
    users = [],
    activeUser
  } = useWBL();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play().then(() => setIsPlaying(true)).catch(() => {});
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  // Resolution order for banners:
  // 1. Regional Page-Specific Override
  // 2. Global Page-Specific Override
  // 3. Regional Global Override
  // 4. Global Custom Image
  // 5. Local Static Asset
  
  const regionalPageBanner = pageKey ? activeTenant?.pageBanners?.[pageKey] : null;
  const globalPageBanner = pageKey ? globalBranding?.pageBanners?.[pageKey] : null;

  const customPageBanner = regionalPageBanner || globalPageBanner;
  
  let heroImageSrc = dashboardHeroBg;
  
  if (customPageBanner) {
    heroImageSrc = customPageBanner;
  } else {
    heroImageSrc = activeTenant?.heroImage || globalBranding?.heroImage || dashboardHeroBg;
  }

  const showVideo = !!globalBranding?.heroVideo && !customPageBanner;
  const oneWordTitle = title ? title.trim().split(/\s+/)[0] : '';

  const availableTermsList = useMemo(() => {
    const rawList = systemConfig?.availableTerms && systemConfig.availableTerms.length > 0
      ? systemConfig.availableTerms
      : ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027'];
    return Array.from(new Set(rawList.filter(Boolean)));
  }, [systemConfig?.availableTerms]);

  // Calculate small coordinating number for each term based on active page context
  const getTermCount = (termName: string): number | null => {
    // 0. Explicit caller override
    if (termCounts && termCounts[termName] !== undefined) {
      return termCounts[termName];
    }

    const normKey = (pageKey || '').toLowerCase();
    const normTitle = (title || '').toLowerCase();

    // Only display numbers on the Opportunities page
    if (normKey === 'opportunities' || normTitle.includes('opportunit')) {
      return (opportunities || []).filter(opp => {
        if (opp.status === 'archived') return false;
        const matchesTerm = opp.semester === termName || opp.term === termName;
        return matchesTerm || (!opp.semester && !opp.term && termName === systemConfig?.currentTerm);
      }).length;
    }

    // Everywhere else: hide term numbers completely
    return null;
  };

  return (
    <div 
      className={`relative w-full overflow-hidden shadow-sm transition-all duration-700 ${className}`} 
      style={{ 
        minHeight: height === '0px' ? '0px' : height, 
        height: height === '0px' ? '0px' : height 
      }}
    >
      {showVideo ? (
        <>
          <video
            ref={videoRef}
            src={globalBranding.heroVideo}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover z-0"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          {/* Subtle discrete pause/play toggle */}
          <button
            type="button"
            onClick={togglePlayPause}
            aria-label={isPlaying ? "Pause background video" : "Play background video"}
            title={isPlaying ? "Pause background video" : "Play background video"}
            className="absolute top-2.5 right-3 z-30 w-6 h-6 rounded-full bg-black/30 hover:bg-black/60 text-white/60 hover:text-white backdrop-blur-xs border border-white/10 flex items-center justify-center transition-all opacity-40 hover:opacity-100 cursor-pointer pointer-events-auto"
          >
            {isPlaying ? (
              <Pause className="w-2.5 h-2.5" />
            ) : (
              <Play className="w-2.5 h-2.5 fill-current ml-0.5" />
            )}
          </button>
        </>
      ) : (
        <img 
          src={heroImageSrc}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      )}
      <div 
        className="absolute inset-0 bg-slate-900 z-5" 
        style={{ opacity: overlayOpacity }}
      />
      
      {/* Absolute Bottom-Anchored Banner Content */}
      <div className="absolute inset-x-0 bottom-0 z-10 pb-3 sm:pb-3.5">
        <div className="w-[95%] mx-auto flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4">
          {/* Left Side: Title */}
          <div className="flex flex-col">
            <h1 className="text-3xl sm:text-4xl font-light tracking-widest text-white uppercase font-outfit leading-tight">{oneWordTitle}</h1>
          </div>
            
          {/* Right Side: Term Selector & Children moved down to bottom */}
          <div className="flex flex-wrap items-end gap-2.5 self-start sm:self-end">
            {!hideTermSelector && (
              <div 
                className="inline-flex items-center gap-0.5 p-1 bg-black/25 backdrop-blur-md border border-white/10 rounded-xl max-w-full overflow-x-auto shadow-xs"
                role="tablist"
                aria-label="Active Academic Term Selector"
              >
                {availableTermsList.map(term => {
                  const isActive = selectedTerm === term;
                  const count = getTermCount(term);
                  return (
                    <button
                      key={term}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setSelectedTerm(term)}
                      title={`Academic Term: ${term}${count !== null ? ` • ${count} items` : ''}`}
                      className={`group relative inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1 rounded-lg text-xs transition-all duration-150 cursor-pointer ${
                        isActive
                          ? 'bg-white/20 text-white font-semibold shadow-xs border border-white/20'
                          : 'text-white/60 hover:text-white hover:bg-white/10 font-normal'
                      }`}
                    >
                      <span className="tracking-tight">{term}</span>
                      {count !== null && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9.5px] font-mono rounded tabular-nums leading-none transition-colors ${
                            isActive
                              ? 'bg-white/20 text-white font-medium'
                              : 'text-white/40 group-hover:text-white/70'
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {children && (
              <div className="flex items-center">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
